"use client";

import { ColumnDef } from "@tanstack/react-table";
import { DataTableColumnHeader } from "./data-table-column-header";
import { DataTableRowActionsCraft } from "./data-table-row-actions-craft";
import Link from "next/link";
import moment from "moment";

// Define the Opportunity type with lead data
export type CraftOpportunity = {
  id: string;
  created_on?: Date | string | null;
  createdAt?: Date | string | null;
  name?: string | null;
  description?: string | null;
  budget?: number | null;
  expected_revenue?: number | null;
  account?: string | null;
  sales_stage?: string | null;
  sales_stage2?: string | null;
  sales_stage3?: string | null;
  assigned_account?: {
    name?: string | null;
  } | null;
  assigned_leads?: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    city?: string | null;
    state?: string | null;
    advisorName?: string | null;
    advisorEmail?: string | null;
    assetInterests?: string[];
    managedByUser?: {
      name?: string | null;
    } | null;
  } | null;
};

export const craftColumns: ColumnDef<CraftOpportunity>[] = [
  {
    id: "actions",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Actions" />
    ),
    cell: ({ row }) => <DataTableRowActionsCraft row={row} />,
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "created_on",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created" />
    ),
    cell: ({ row }) => {
      const date = row.getValue("created_on") || row.original.createdAt;
      return (
        <div className="w-[80px]">
          {date ? moment(date).format("MM-DD-YY") : "N/A"}
        </div>
      );
    },
    enableSorting: true,
    enableHiding: false,
  },
  {
    accessorKey: "name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Deal Name" />
    ),
    cell: ({ row }) => (
      <div className="max-w-[200px] truncate">
        {row.getValue("name") || "N/A"}
      </div>
    ),
    enableSorting: true,
    enableHiding: true,
  },
  {
    id: "account",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Account" />
    ),
    cell: ({ row }) => {
      return <div className="w-[100px]">Craft Capital</div>;
    },
    enableSorting: false,
    enableHiding: true,
  },
  {
    accessorKey: "budget",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Amount" />
    ),
    cell: ({ row }) => {
      const amount = row.getValue("budget") as number | null;
      return (
        <div className="w-[100px]">
          {amount
            ? new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(amount)
            : "N/A"}
        </div>
      );
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    id: "city",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="City" />
    ),
    cell: ({ row }) => {
      const city = row.original.assigned_leads?.city;
      return <div className="w-[100px]">{city || "N/A"}</div>;
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    id: "state",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="State" />
    ),
    cell: ({ row }) => {
      const state = row.original.assigned_leads?.state;
      return <div className="w-[60px]">{state || "N/A"}</div>;
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    id: "advisor",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Advisor" />
    ),
    cell: ({ row }) => {
      const advisor = row.original.assigned_leads?.advisorName;
      return <div className="max-w-[150px] truncate">{advisor || "N/A"}</div>;
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    id: "assetInterests",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Asset Purchase Interest" />
    ),
    cell: ({ row }) => {
      const assetInterests = row.original.assigned_leads?.assetInterests;
      
      // Map asset interest values to readable labels
      const assetLabels: Record<string, string> = {
        equipment: "Equipment",
        open_to_others: "Open to Others",
        digital_advertising_screens: "Digital Advertising Screens",
        gpu_data_servers: "GPU Data Servers",
      };
      
      if (!assetInterests || assetInterests.length === 0) {
        return <div className="max-w-[200px]">N/A</div>;
      }
      
      const formattedAssets = assetInterests
        .map((asset) => assetLabels[asset] || asset)
        .join(", ");
      
      return (
        <div className="max-w-[200px] truncate" title={formattedAssets}>
          {formattedAssets}
        </div>
      );
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    id: "managedBy",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Managed By" />
    ),
    cell: ({ row }) => {
      const managedBy = row.original.assigned_leads?.managedByUser?.name;
      return <div className="max-w-[150px] truncate">{managedBy || "N/A"}</div>;
    },
    enableSorting: true,
    enableHiding: true,
  },
]; 